import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useFacility } from "@/context/FacilityContext";
import { PageHeader } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Loader2, BedDouble, Trash2, Bed, CircleCheck, CircleDot } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { department: "", bed_number: "", ward: "", bed_type: "General", daily_charge: 2500 };

export default function IpdBeds() {
  const { activeId, has } = useFacility();
  const [beds, setBeds] = useState(null);
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState({ departments: [], bed_types: [] });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => {
    if (!activeId) return;
    setBeds(null);
    api.get(`/ipd/beds?facility_id=${activeId}`).then((r) => setBeds(r.data)).catch(() => setBeds([]));
    api.get(`/ipd/stats?facility_id=${activeId}`).then((r) => setStats(r.data)).catch(() => {});
    api.get(`/ipd/meta?facility_id=${activeId}`).then((r) => setMeta(r.data)).catch(() => {});
  }, [activeId]);
  useEffect(load, [load]);

  const save = async () => {
    if (!form.department || !form.bed_number) { toast.error("Department and bed number are required"); return; }
    setSaving(true);
    try {
      await api.post("/ipd/beds", { ...form, facility_id: activeId, daily_charge: parseFloat(form.daily_charge || 0) });
      toast.success(`Bed ${form.bed_number} added`);
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const remove = async (b) => {
    try { await api.delete(`/ipd/beds/${b.id}`); toast.success("Bed removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const grouped = (beds || []).reduce((acc, b) => { (acc[b.department] = acc[b.department] || []).push(b); return acc; }, {});

  return (
    <div data-testid="ipd-beds-page">
      <PageHeader title="Bed Management" subtitle="Department-wise beds and live occupancy for this facility."
        actions={has("bed:create") && <Button onClick={() => setOpen(true)} className="bg-blue-700 hover:bg-blue-800" data-testid="add-bed-button"><Plus className="h-4 w-4 mr-1.5" /> Add Bed</Button>} />

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total Beds" value={stats.total_beds} icon={Bed} testId="ipd-stat-total" />
          <StatCard label="Occupied" value={stats.occupied} icon={CircleDot} tone="amber" testId="ipd-stat-occupied" />
          <StatCard label="Available" value={stats.available} icon={CircleCheck} tone="emerald" testId="ipd-stat-available" />
          <StatCard label="Occupancy" value={`${stats.occupancy_percent}%`} icon={BedDouble} tone="slate" testId="ipd-stat-occupancy" />
        </div>
      )}

      {beds === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
        beds.length === 0 ? <div className="bg-white border border-slate-200 rounded-md py-16 text-center text-slate-400">No beds yet. Click "Add Bed" to set up your wards.</div> : (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-4 text-[11px] text-slate-500" data-testid="bed-legend">
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Available</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" /> Occupied</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-violet-500" /> Pre-operative (posted for OT)</span>
            </div>
            {Object.keys(grouped).sort().map((dept) => (
              <div key={dept}>
                <div className="text-[11px] font-mono uppercase tracking-widest text-slate-400 mb-2">{dept}</div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {grouped[dept].map((b) => {
                    const preOp = b.status === "occupied" && b.ot_status;
                    const cardCls = preOp
                      ? "bg-violet-50 border-violet-300"
                      : b.status === "occupied" ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200";
                    return (
                    <div key={b.id} data-testid={`bed-card-${b.id}`} className={`rounded-md border p-3 ${cardCls}`} title={preOp ? `Pre-op: ${b.ot_procedure}${b.ot_date ? ` · ${b.ot_date}` : ""}` : ""}>
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-bold text-slate-800">{b.bed_number}</span>
                        {preOp
                          ? <span className="text-[9px] font-semibold uppercase tracking-wide bg-violet-600 text-white rounded px-1.5 py-0.5" data-testid={`bed-preop-${b.id}`}>{b.ot_status === "confirmed" ? "OT" : "OT?"}</span>
                          : (has("bed:delete") && b.status !== "occupied" && (
                            <button onClick={() => remove(b)} className="text-slate-300 hover:text-red-500" data-testid={`bed-delete-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                          ))}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{b.ward || "—"} · {b.bed_type}</div>
                      <div className={`mt-2 inline-flex items-center gap-1 text-[11px] font-medium ${preOp ? "text-violet-700" : b.status === "occupied" ? "text-amber-700" : "text-emerald-700"}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${preOp ? "bg-violet-500" : b.status === "occupied" ? "bg-amber-500" : "bg-emerald-500"}`} />
                        {b.status === "occupied" ? (b.current_patient_name || "Occupied") : "Available"}
                      </div>
                      {preOp && <div className="text-[10px] text-violet-600 mt-1 truncate">Pre-op · {b.ot_procedure}{b.ot_date ? ` (${b.ot_date})` : ""}</div>}
                    </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="add-bed-dialog">
          <DialogHeader>
            <DialogTitle>Add Bed</DialogTitle>
            <DialogDescription>Register a bed under a department. It starts as available.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Department</Label>
              <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="bed-department-select">
                <option value="">Select…</option>
                {meta.departments.map((d) => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Bed number</Label><Input value={form.bed_number} onChange={(e) => setForm({ ...form, bed_number: e.target.value })} placeholder="e.g. GM-105" data-testid="bed-number-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Ward / Room</Label><Input value={form.ward} onChange={(e) => setForm({ ...form, ward: e.target.value })} placeholder="e.g. General Ward A" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Bed type</Label>
                <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.bed_type} onChange={(e) => setForm({ ...form, bed_type: e.target.value })} data-testid="bed-type-select">
                  {(meta.bed_types || ["General", "Semi-Private", "Private", "Deluxe", "ICU"]).map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Daily charge (₹)</Label><Input type="number" value={form.daily_charge} onChange={(e) => setForm({ ...form, daily_charge: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="bed-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add bed"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
